<script lang="ts" setup>
import { mdiEmailOutline, mdiFacebook, mdiInstagram, mdiLink, mdiTwitter, mdiYoutube } from '@mdi/js'
import { Studio } from '@/modules/Studios/types'
import { useImage } from '@/shared/composables/image'
import { computed } from 'vue'

const props = defineProps<{
  studio: Studio
}>()

const { getStudioImage } = useImage()
const hasSocials = computed(() => {
  return Object.values(props.studio.socials).some((value) => value)
})
</script>

<template>
  <div class="information">
    <v-img
      :src="getStudioImage(studio.image, 1000)"
      alt="Studio image"
      class="image"
    >
      <v-btn
        v-if="studio.canEdit"
        :to="{
          name: $vuetify.breakpoint.sm || $vuetify.breakpoint.xs ? 'StudioSettings' : 'StudioSettingsGeneral',
          params: { id: studio.id },
        }"
        aria-label="Studio settings"
        class="settings-btn"
        color="var(--color-blue-800)"
      >
        <span class="options-icon" />
        Settings
      </v-btn>
    </v-img>
    <div class="main">
      <h2 class="studio-name">
        {{ studio.name }}
      </h2>

      <div class="divider" />

      <div
        v-if="hasSocials"
        class="socials"
      >
        <a
          v-if="studio.socials.email"
          :href="`mailto:${studio.socials.email}`"
          aria-label="Email"
          class="social"
        >
          <v-icon>{{ mdiEmailOutline }}</v-icon>
        </a>

        <a
          v-if="studio.socials.facebook"
          :href="studio.socials.facebook"
          aria-label="Facebook"
          class="social"
        >
          <v-icon>{{ mdiFacebook }}</v-icon>
        </a>

        <a
          v-if="studio.socials.instagram"
          :href="studio.socials.instagram"
          aria-label="Instagram"
          class="social"
        >
          <v-icon>{{ mdiInstagram }}</v-icon>
        </a>

        <a
          v-if="studio.socials.twitter"
          :href="studio.socials.twitter"
          aria-label="Twitter"
          class="social"
        >
          <v-icon>{{ mdiTwitter }}</v-icon>
        </a>

        <a
          v-if="studio.socials.youtube"
          :href="studio.socials.youtube"
          aria-label="YouTube"
          class="social"
        >
          <v-icon>{{ mdiYoutube }}</v-icon>
        </a>

        <a
          v-if="studio.socials.website"
          :href="studio.socials.website"
          aria-label="Website"
          class="social"
        >
          <v-icon>{{ mdiLink }}</v-icon>
        </a>
      </div>

      <div
        v-if="hasSocials"
        class="divider"
      />

      <div class="stats">
        <div class="stat">
          <span class="stat-icon stat-icon--members" />
          <span class="stat-name">Students</span>
          <div class="stat-val">
            {{ studio.totalStudents }}
          </div>
        </div>

        <div class="stat">
          <span class="stat-icon stat-icon--events" />
          <span class="stat-name">Battles Entered</span>
          <div class="stat-val">
            {{ studio.battlesEntered }}
          </div>
        </div>

        <div class="stat">
          <span class="stat-icon stat-icon--points" />
          <span class="stat-name">Total Wins</span>
          <div class="stat-val">
            {{ studio.totalWins }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.information {
  display: flex;
  gap: 40px;
  align-items: flex-start;

  .image {
    position: relative;
    flex: 0 0 420px;
    width: 100%;
    max-width: 420px;
    height: auto;
    margin: 0 auto;
    border: 1px solid var(--color-grey-600);
    border-radius: 12px !important;
    aspect-ratio: 1 / 1;

    @media (max-width: $screen-sm) {
      flex: 0 0 100%;
    }

    .settings-btn {
      position: absolute;
      top: 12px;
      left: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 48px;
      padding: 0 20px;
      color: var(--color-white);
      font-weight: var(--font-weight-bold);
      font-size: 14px;
      text-transform: uppercase;
      border: 3px solid var(--color-white) !important;
      border-radius: 9px;
      box-shadow: 0 4px 12px rgb(0 0 0 / 12%);

      .options-icon {
        width: 22px;
        height: 22px;
        margin-right: 4px;
        background: url('/img/ic_Settings_White.svg') no-repeat center;

        @media (max-width: $screen-md) {
          width: 16px;
          height: 16px;
          background-size: 16px 16px;
        }
      }
    }
  }

  @media (max-width: $screen-md) {
    flex-direction: column;
    gap: 20px;
  }
}

.main {
  display: flex;
  flex: 1 1;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;
  justify-content: center;
  width: 100%;

  .studio-name {
    @include font-h1;

    color: var(--color-dark-800);
  }

  .divider {
    width: 100%;
    height: 1px;
    background-color: var(--color-grey-600);
  }

  .socials {
    display: flex;
    gap: 8px;

    .social {
      width: 40px;
      height: 40px;
      cursor: pointer;

      .v-icon {
        display: flex;
        height: 100%;
        margin: auto;
        color: var(--color-black);
      }
    }
  }

  .stats {
    display: flex;
    gap: 16px;

    @media (max-width: $screen-sm) {
      flex-direction: column;
    }
  }

  .stat {
    display: flex;
    flex: 1 1;
    flex-direction: column;
    align-items: center;

    @media (max-width: $screen-sm) {
      flex-direction: row;
    }

    .stat-icon {
      display: inline-block;
      width: 24px;
      height: 24px;
      margin-bottom: 6px;

      @media (max-width: $screen-sm) {
        margin-right: 8px;
        margin-bottom: 0;
      }

      &--members {
        background: url('/img/ic_Avatar.svg') no-repeat center;
      }

      &--events {
        background: url('/img/ic_Compete.svg') no-repeat center;
      }

      &--points {
        background: url('/img/ic_Ribbon.svg') no-repeat center;
      }
    }

    .stat-name {
      margin-bottom: 10px;
      color: var(--color-dark-800);

      @media (max-width: $screen-sm) {
        margin-right: auto;
        margin-bottom: 0;
      }
    }

    .stat-val {
      color: var(--color-dark-800);
      font-weight: var(--font-weight-bold);
      font-size: 24px;
      line-height: 30px;
    }
  }
}
</style>
